/**
 * Tela /reset-password?token= — conclusão do reset de senha (PSHWO-67053).
 *
 * Destino do link gerado por `POST /auth/forgot-password` e pelo "Resetar
 * senha" da tela de Usuários. Pública (sem RequireAuth). Envia
 * `POST /api/auth/reset-password {token, new_password}`; em 204 leva ao
 * login com `?reset=ok`, que mostra o aviso de sucesso.
 *
 * A política espelha a da API (`_validate_password_strength`): mínimo 10
 * caracteres, 1 maiúscula e 1 dígito. A API continua sendo a autoridade — o
 * 422 dela também é tratado.
 *
 * Token inválido, expirado ou já usado chega como 400 com mensagem única
 * (a API não diz qual foi o caso); a tela repete isso sem detalhar.
 */

import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { z } from "zod";

import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";

/** Search params aceitos pela rota /reset-password. */
export interface ResetPasswordSearch {
  token?: string;
}

/** `validateSearch` da rota — descarta token vazio ou não-string. */
export function validateResetPasswordSearch(
  raw: Record<string, unknown>,
): ResetPasswordSearch {
  return typeof raw.token === "string" && raw.token.trim() !== ""
    ? { token: raw.token.trim() }
    : {};
}

function buildSchema(t: TFunction) {
  return z
    .object({
      new_password: z
        .string()
        .min(10, t("auth.resetPassword.errors.policy"))
        .max(200, t("auth.resetPassword.errors.policy"))
        .regex(/[A-Z]/, t("auth.resetPassword.errors.policy"))
        .regex(/\d/, t("auth.resetPassword.errors.policy")),
      confirm_password: z
        .string()
        .min(1, t("auth.resetPassword.errors.confirmRequired")),
    })
    .refine((d) => d.new_password === d.confirm_password, {
      message: t("auth.resetPassword.errors.mismatch"),
      path: ["confirm_password"],
    });
}

type FormData = z.infer<ReturnType<typeof buildSchema>>;

type ServerError = "invalidLink" | "policy" | "tooMany" | "network" | "generic";

function errorFromStatus(status: number): ServerError {
  if (status === 400) return "invalidLink";
  if (status === 422) return "policy";
  if (status === 429) return "tooMany";
  return "generic";
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useSearch({ strict: false }) as ResetPasswordSearch;
  const [serverError, setServerError] = useState<ServerError | null>(null);

  const schema = useMemo(() => buildSchema(t), [t]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  async function onSubmit(data: FormData) {
    setServerError(null);
    let res: Response;
    try {
      res = await fetch("/api/auth/reset-password", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, new_password: data.new_password }),
      });
    } catch {
      setServerError("network");
      return;
    }
    if (!res.ok) {
      setServerError(errorFromStatus(res.status));
      return;
    }
    navigate({ to: "/login", search: { reset: "ok" }, replace: true });
  }

  return (
    <AuthLayout>
      <header className="mb-8">
        <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
          {t("auth.resetPassword.eyebrow")}
        </div>
        <h2 className="text-[26px] font-semibold tracking-[-0.01em] text-[var(--color-foreground)]">
          {t("auth.resetPassword.title")}
        </h2>
        <p className="mt-2 text-[14px] text-[var(--color-muted-foreground)]">
          {token
            ? t("auth.resetPassword.subtitle")
            : t("auth.resetPassword.missingToken")}
        </p>
      </header>

      {token ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          noValidate
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new_password">
              {t("auth.resetPassword.newPassword")}
            </Label>
            <Input
              id="new_password"
              type="password"
              autoComplete="new-password"
              autoFocus
              aria-invalid={errors.new_password ? true : undefined}
              aria-describedby="new_password_hint"
              {...register("new_password")}
            />
            {errors.new_password ? (
              <span
                role="alert"
                className="text-xs text-[var(--color-destructive)]"
              >
                {errors.new_password.message}
              </span>
            ) : (
              <span
                id="new_password_hint"
                className="text-xs text-[var(--color-muted-foreground)]"
              >
                {t("auth.resetPassword.policyHint")}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm_password">
              {t("auth.resetPassword.confirmPassword")}
            </Label>
            <Input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.confirm_password ? true : undefined}
              {...register("confirm_password")}
            />
            {errors.confirm_password ? (
              <span
                role="alert"
                className="text-xs text-[var(--color-destructive)]"
              >
                {errors.confirm_password.message}
              </span>
            ) : null}
          </div>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {t(`auth.resetPassword.errors.${serverError}`)}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={isSubmitting}
            className="mt-2 w-full"
          >
            {isSubmitting
              ? t("auth.resetPassword.submitting")
              : t("auth.resetPassword.submit")}
          </Button>
        </form>
      ) : null}

      <p className="mt-6 text-center text-sm text-[var(--color-muted-foreground)]">
        <Link
          to="/login"
          className="text-[var(--color-foreground)] hover:text-[var(--color-primary)]"
        >
          {t("auth.resetPassword.backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
